import Ajv, { ErrorObject } from 'ajv';

import advisoryCatalogueManifest from './advisory-catalogue.v0.1.json';
import {
  ADVISORY_COMPONENT_IDS,
  ADVISORY_COMPOSITE_IDS,
  ADVISORY_ENGAGEMENT_IDS,
  ADVISORY_QUESTION_IDS,
  AdvisoryComponentId,
  AdvisoryCompositeId,
  AdvisoryEngagementId,
  AdvisoryQuestionId,
} from './catalogue-ids';
import compositeAnswerSchema from './composite-answer.v0.1.schema.json';
import competitorAnalysisManifest from './competitor-analysis.v0.1.json';

export {
  ADVISORY_COMPONENT_IDS,
  ADVISORY_COMPOSITE_IDS,
  ADVISORY_ENGAGEMENT_IDS,
  ADVISORY_QUESTION_IDS,
};
export type { AdvisoryComponentId, AdvisoryCompositeId, AdvisoryEngagementId, AdvisoryQuestionId };

export const COMPOSITE_ANSWER_VERSION = '0.1' as const;
export const COMPETITOR_ANALYSIS_ENGAGEMENT_ID = 'competitor-analysis' as const;

export const SECTION_ROLES = ['primary', 'optional', 'slide'] as const;
export type SectionRole = (typeof SECTION_ROLES)[number];

export const LAYOUT_PRESETS = [
  'hero-grid',
  'split-analysis',
  'narrative-table',
  'full-visual',
  'continuation',
] as const;
export type LayoutPreset = (typeof LAYOUT_PRESETS)[number];

export type CompetitorAnalysisQuestionId =
  | 'market-attractiveness'
  | 'right-to-win'
  | 'competitive-durability'
  | 'emerging-risks'
  | 'competitors-attack-collaborate-acquire'
  | 'strategic-synthesis';

export type CompetitorAnalysisCompositeId =
  | 'DecisionBrief'
  | 'OptionSelectionBoard'
  | 'MarketOpportunityBoard'
  | 'DurabilityBoard'
  | 'RiskExposureBoard';

export type CompetitorAnalysisComponentId =
  | 'decision'
  | 'action-priority'
  | 'counter-position'
  | 'options-matrix'
  | 'sensitivity-plot'
  | 'market-sizing'
  | 'portfolio-matrix'
  | 'durability-factors'
  | 'risk-matrix'
  | 'people-grid'
  | 'evidence-table'
  | 'metric-strip'
  | 'narrative';

type DisplayString = string;
type DisplayList = string[];
type DisplayRows<Row> = Row[];
type StringRow<Column extends string> = { [Key in Column]: string };

/** Runtime section data mirrors Bardh's rendering payload. */
export interface ComponentDataV01 {
  'action-priority': {
    rows?: DisplayRows<StringRow<'action' | 'reinforces' | 'owner_or_horizon' | 'priority'>>;
  };
  'business-model-canvas': {
    rows?: DisplayRows<StringRow<'block' | 'current_configuration' | 'fit' | 'evidence'>>;
    constraining_block?: DisplayString;
    why_constraining?: DisplayString;
    alternative?: DisplayString;
    comparable?: DisplayString;
  };
  'comparison-matrix': {
    basis?: DisplayString;
    rows?: DisplayRows<StringRow<'line' | 'subject' | 'value'>>;
  };
  'counter-position': {
    rows?: DisplayRows<StringRow<'assumption' | 'support' | 'counter_evidence' | 'status'>>;
    flip_conditions?: DisplayList;
    smallest_test?: DisplayString;
  };
  decision: {
    decision?: DisplayString;
    rationale?: DisplayString;
    options?: DisplayRows<StringRow<'option' | 'terms' | 'recommended'>>;
    gates?: DisplayRows<
      StringRow<'gate' | 'threshold' | 'if_met' | 'if_missed' | 'current_position'>
    >;
    investment?: DisplayString;
    forgone?: DisplayList;
  };
  'durability-factors': {
    advantage?: DisplayString;
    mechanism?: DisplayString;
    customer_value?: DisplayString;
    buildable?: 'yes' | 'partially' | 'no' | 'not_established';
    rows?: DisplayRows<StringRow<'barrier' | 'type' | 'durable_until' | 'note'>>;
    choice_factors?: DisplayRows<
      StringRow<'factor' | 'weight' | 'company' | 'competitors' | 'source'>
    >;
    structure?: DisplayRows<StringRow<'force' | 'rating' | 'evidence'>>;
  };
  'evidence-table': {
    rows?: DisplayRows<StringRow<'claim' | 'source' | 'locator' | 'status'>>;
    not_established?: DisplayList;
  };
  'financial-forecast': {
    basis?: DisplayString;
    rows?: DisplayRows<StringRow<'line' | 'period' | 'value'>>;
  };
  'market-sizing': {
    basis?: DisplayString;
    rows?: DisplayRows<StringRow<'candidate' | 'size' | 'growth' | 'source' | 'source_date'>>;
    divergences?: DisplayList;
    position?: DisplayRows<StringRow<'candidate' | 'tam' | 'sam' | 'company_revenue' | 'share'>>;
    whitespace?: DisplayRows<StringRow<'pool' | 'size' | 'growth' | 'why_underserved'>>;
  };
  'metric-strip': {
    rows?: DisplayRows<StringRow<'label' | 'value' | 'unit' | 'period' | 'source'>>;
  };
  narrative: {
    body?: DisplayString;
  };
  'options-matrix': {
    criteria?: DisplayRows<StringRow<'criterion' | 'weight'>>;
    rows?: DisplayRows<StringRow<'option' | 'scores' | 'total' | 'rank'>>;
    shortlisting_criterion?: DisplayString;
    excluded?: DisplayRows<StringRow<'option' | 'reason'>>;
    confidence_notes?: DisplayList;
  };
  'people-grid': {
    rows?: DisplayRows<StringRow<'role_or_capability' | 'current_state' | 'gap' | 'action'>>;
  };
  'portfolio-matrix': {
    x_axis?: DisplayString;
    y_axis?: DisplayString;
    rows?: DisplayRows<StringRow<'item' | 'x' | 'y' | 'size' | 'note'>>;
  };
  'risk-matrix': {
    rows?: DisplayRows<StringRow<'risk' | 'likelihood' | 'impact' | 'mitigation' | 'owner'>>;
  };
  'roadmap-timeline': {
    rows?: DisplayRows<StringRow<'phase' | 'window' | 'trigger' | 'owner' | 'status'>>;
  };
  'scenario-matrix': {
    rows?: DisplayRows<StringRow<'scenario' | 'assumptions' | 'outcome' | 'probability'>>;
  };
  'sensitivity-plot': {
    variable?: DisplayString;
    outcome?: DisplayString;
    rows?: DisplayRows<StringRow<'case' | 'variable_value' | 'outcome_value'>>;
    breakpoint?: DisplayString;
  };
  'system-map': {
    nodes?: DisplayRows<StringRow<'node' | 'role'>>;
    edges?: DisplayRows<StringRow<'from' | 'to' | 'relationship'>>;
  };
  'value-bridge': {
    from_label?: DisplayString;
    to_label?: DisplayString;
    rows?: DisplayRows<StringRow<'driver' | 'delta' | 'note'>>;
  };
}

type ComponentField<ComponentId extends AdvisoryComponentId> = Extract<
  keyof ComponentDataV01[ComponentId],
  string
>;

export interface CompositeSectionV01<ComponentId extends AdvisoryComponentId> {
  componentId: ComponentId;
  title: string;
  role: SectionRole;
  filledFrom: string;
  fields: ComponentField<ComponentId>[];
  data: ComponentDataV01[ComponentId];
}

type SectionForComponentV01<ComponentId extends AdvisoryComponentId> =
  ComponentId extends AdvisoryComponentId ? CompositeSectionV01<ComponentId> : never;

export type AnyCompositeSectionV01 = SectionForComponentV01<AdvisoryComponentId>;

export interface OmittedCompositeSectionV01 {
  componentId: AdvisoryComponentId;
  title: string;
  role: SectionRole;
  reason: string;
}

export interface CompositeSlideV01 {
  layout: LayoutPreset;
  sections: AdvisoryComponentId[];
}

export interface CompositeSlidePlansV01 {
  short: CompositeSlideV01[];
  long: CompositeSlideV01[];
}

/** Exact v0.1 shape stored at `answer.rendering`. */
export interface CompositeAnswerV01 {
  compositeId: AdvisoryCompositeId;
  version: 1;
  filledBy: string;
  primaryFilled: boolean;
  sections: AnyCompositeSectionV01[];
  omitted: OmittedCompositeSectionV01[];
  slidePlans: CompositeSlidePlansV01;
}

/** Identity supplied by the answer path/project or Supervisor event. */
export interface CompositeAnswerIdentityV01 {
  engagementId: AdvisoryEngagementId;
  questionId: AdvisoryQuestionId;
}

export type ComponentAttributeType = 'text' | 'markdown' | 'list' | 'rows' | 'currency' | 'enum';

export interface ComponentAttributeDefinitionV01 {
  readonly name: string;
  readonly type: ComponentAttributeType;
  readonly required: boolean;
  readonly columns?: readonly string[];
  readonly options?: readonly string[];
  readonly description: string;
}

export interface ComponentSchemaDefinitionV01 {
  readonly componentId: AdvisoryComponentId;
  readonly purpose: string;
  readonly attributes: readonly ComponentAttributeDefinitionV01[];
}

export interface CompositeSectionDescriptorV01 {
  readonly componentId: AdvisoryComponentId;
  readonly title: string;
}

export interface CompositeDefinitionV01 {
  readonly compositeId: AdvisoryCompositeId;
  readonly version: 1;
  readonly ontologyDocumentState: 'present' | 'missing';
  readonly purpose: string;
  readonly primarySection: CompositeSectionDescriptorV01;
  readonly optionalSections: readonly CompositeSectionDescriptorV01[];
  readonly slideSections: readonly CompositeSectionDescriptorV01[];
  readonly slidePlans: Readonly<{
    short: readonly Readonly<CompositeSlideV01>[];
    long: readonly Readonly<CompositeSlideV01>[];
  }>;
}

export interface QuestionDefinitionV01 {
  readonly questionId: AdvisoryQuestionId;
  readonly compositeId: AdvisoryCompositeId;
  readonly engagementIds: readonly AdvisoryEngagementId[];
}

export interface EngagementDefinitionV01 {
  readonly engagementId: AdvisoryEngagementId;
  readonly name: string;
  readonly fillOrder: readonly AdvisoryQuestionId[];
  readonly readOrder: readonly AdvisoryQuestionId[];
  readonly questionIds: readonly AdvisoryQuestionId[];
}

interface RuntimeContractV01 {
  readonly target: 'answer.rendering';
  readonly publicationState: 'published-development';
  readonly identityContext: readonly ['engagementId', 'questionId'];
  readonly sectionFillAttribution: 'filledFrom';
  readonly provenance: 'deferred';
  readonly numericChartData: 'deferred';
}

export interface AdvisoryCatalogueManifestV01 {
  readonly contractVersion: typeof COMPOSITE_ANSWER_VERSION;
  readonly catalogueSource: {
    readonly repository: string;
    readonly commit: string;
    readonly engagementRoot: string;
    readonly keyQuestionRoot: string;
    readonly firestoreDigest: string;
  };
  readonly ontologySnapshot: {
    readonly projectId: string;
    readonly database: string;
    readonly documentRoot: string;
    readonly publishedAt: string;
    readonly observedOn: string;
  };
  readonly displayOrderAuthority: 'backend';
  readonly engagementCount: 21;
  readonly questionCount: 96;
  readonly compositeCount: 24;
  readonly componentCount: 20;
  readonly runtimeContract: RuntimeContractV01;
  readonly sectionRoles: readonly SectionRole[];
  readonly layoutPresets: readonly LayoutPreset[];
  readonly engagements: readonly EngagementDefinitionV01[];
  readonly questions: readonly QuestionDefinitionV01[];
  readonly composites: readonly CompositeDefinitionV01[];
  readonly componentSchemas: readonly ComponentSchemaDefinitionV01[];
}

export interface CompetitorAnalysisManifestV01 {
  readonly contractVersion: typeof COMPOSITE_ANSWER_VERSION;
  readonly catalogueSource: {
    readonly repository: string;
    readonly commit: string;
    readonly engagementPath: string;
  };
  readonly ontologySnapshot: {
    readonly projectId: string;
    readonly database: string;
    readonly documentRoot: string;
    readonly observedOn: string;
  };
  readonly engagementId: typeof COMPETITOR_ANALYSIS_ENGAGEMENT_ID;
  readonly displayOrderAuthority: 'backend';
  readonly runtimeContract: RuntimeContractV01;
  readonly sectionRoles: readonly SectionRole[];
  readonly layoutPresets: readonly LayoutPreset[];
  readonly fillOrder: readonly CompetitorAnalysisQuestionId[];
  readonly readOrder: readonly CompetitorAnalysisQuestionId[];
  readonly questions: readonly {
    readonly questionId: CompetitorAnalysisQuestionId;
    readonly compositeId: CompetitorAnalysisCompositeId;
  }[];
  readonly composites: readonly (Omit<CompositeDefinitionV01, 'compositeId' | 'purpose'> & {
    readonly compositeId: CompetitorAnalysisCompositeId;
  })[];
  readonly componentSchemas: readonly (Omit<ComponentSchemaDefinitionV01, 'componentId'> & {
    readonly componentId: CompetitorAnalysisComponentId;
  })[];
}

export const ADVISORY_COMPOSITE_CONTRACT =
  advisoryCatalogueManifest as unknown as AdvisoryCatalogueManifestV01;
export const COMPETITOR_ANALYSIS_CONTRACT =
  competitorAnalysisManifest as unknown as CompetitorAnalysisManifestV01;

export const ENGAGEMENT_DEFINITIONS: Readonly<
  Record<AdvisoryEngagementId, EngagementDefinitionV01>
> = Object.freeze(
  ADVISORY_COMPOSITE_CONTRACT.engagements.reduce(
    (definitions, definition) => ({ ...definitions, [definition.engagementId]: definition }),
    {} as Record<AdvisoryEngagementId, EngagementDefinitionV01>
  )
);

export const QUESTION_DEFINITIONS: Readonly<Record<AdvisoryQuestionId, QuestionDefinitionV01>> =
  Object.freeze(
    ADVISORY_COMPOSITE_CONTRACT.questions.reduce(
      (definitions, definition) => ({ ...definitions, [definition.questionId]: definition }),
      {} as Record<AdvisoryQuestionId, QuestionDefinitionV01>
    )
  );

export const COMPOSITE_BY_QUESTION: Readonly<Record<AdvisoryQuestionId, AdvisoryCompositeId>> =
  Object.freeze(
    ADVISORY_COMPOSITE_CONTRACT.questions.reduce(
      (mappings, question) => ({ ...mappings, [question.questionId]: question.compositeId }),
      {} as Record<AdvisoryQuestionId, AdvisoryCompositeId>
    )
  );

export const COMPOSITE_DEFINITIONS: Readonly<Record<AdvisoryCompositeId, CompositeDefinitionV01>> =
  Object.freeze(
    ADVISORY_COMPOSITE_CONTRACT.composites.reduce(
      (definitions, definition) => ({ ...definitions, [definition.compositeId]: definition }),
      {} as Record<AdvisoryCompositeId, CompositeDefinitionV01>
    )
  );

export const COMPONENT_SCHEMAS: Readonly<
  Record<AdvisoryComponentId, ComponentSchemaDefinitionV01>
> = Object.freeze(
  ADVISORY_COMPOSITE_CONTRACT.componentSchemas.reduce(
    (schemas, schema) => ({ ...schemas, [schema.componentId]: schema }),
    {} as Record<AdvisoryComponentId, ComponentSchemaDefinitionV01>
  )
);

export interface ResolvedCompositeSectionDescriptorV01 extends CompositeSectionDescriptorV01 {
  readonly role: SectionRole;
}

export const getCompositeSectionDescriptors = (
  composite: CompositeDefinitionV01
): readonly ResolvedCompositeSectionDescriptorV01[] => [
  { ...composite.primarySection, role: 'primary' },
  ...composite.optionalSections.map(section => ({ ...section, role: 'optional' as const })),
  ...composite.slideSections.map(section => ({ ...section, role: 'slide' as const })),
];

export interface ValidationIssue {
  readonly path: string;
  readonly code: string;
  readonly message: string;
}

export type CompositeAnswerValidationResult =
  | { ok: true; value: CompositeAnswerV01 }
  | { ok: false; issues: readonly ValidationIssue[] };

const ajv = new Ajv({ allErrors: true, strict: true });
const validateSchema = ajv.compile<CompositeAnswerV01>(compositeAnswerSchema);

const escapeJsonPointerToken = (token: string): string =>
  token.replace(/~/g, '~0').replace(/\//g, '~1');

const schemaErrorPath = (error: ErrorObject): string => {
  if (error.keyword === 'required' && 'missingProperty' in error.params) {
    return `${error.instancePath}/${escapeJsonPointerToken(String(error.params.missingProperty))}`;
  }
  if (error.keyword === 'additionalProperties' && 'additionalProperty' in error.params) {
    return `${error.instancePath}/${escapeJsonPointerToken(
      String(error.params.additionalProperty)
    )}`;
  }
  return error.instancePath;
};

const schemaIssues = (errors: ErrorObject[] | null | undefined): ValidationIssue[] => {
  const issues = (errors ?? []).map(error => ({
    path: schemaErrorPath(error),
    code: `schema.${error.keyword}`,
    message: error.message ?? 'Schema validation failed',
  }));
  return issues.filter(
    (issue, index) =>
      issues.findIndex(
        candidate =>
          candidate.path === issue.path &&
          candidate.code === issue.code &&
          candidate.message === issue.message
      ) === index
  );
};

const sameStringSet = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every(value => right.includes(value));

const canonicalizeJsonObjectKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(canonicalizeJsonObjectKeys);
  }
  if (value !== null && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((canonical, key) => {
        canonical[key] = canonicalizeJsonObjectKeys((value as Record<string, unknown>)[key]);
        return canonical;
      }, {});
  }
  return value;
};

const sameJson = (left: unknown, right: unknown): boolean =>
  JSON.stringify(canonicalizeJsonObjectKeys(left)) ===
  JSON.stringify(canonicalizeJsonObjectKeys(right));

const semanticIssues = (
  answer: CompositeAnswerV01,
  identity: CompositeAnswerIdentityV01
): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const question = QUESTION_DEFINITIONS[identity.questionId];

  if (!question) {
    return [
      {
        path: '/questionId',
        code: 'identity.unknown_question',
        message: `${String(identity.questionId)} is not a canonical question`,
      },
    ];
  }

  if (answer.compositeId !== question.compositeId) {
    issues.push({
      path: '/compositeId',
      code: 'identity.question_composite_mismatch',
      message: `${identity.questionId} must use ${question.compositeId}`,
    });
  }

  if (!question.engagementIds.includes(identity.engagementId)) {
    issues.push({
      path: '/engagementId',
      code: 'identity.question_engagement_mismatch',
      message: `${identity.questionId} does not belong to ${identity.engagementId}`,
    });
  }

  const composite = COMPOSITE_DEFINITIONS[answer.compositeId];
  const descriptors = getCompositeSectionDescriptors(composite);
  const descriptorsById = new Map(
    descriptors.map(descriptor => [descriptor.componentId, descriptor])
  );
  const observedIds = new Set<AdvisoryComponentId>();

  const checkDescriptor = (
    section: Pick<AnyCompositeSectionV01, 'componentId' | 'role'>,
    path: string
  ): void => {
    const descriptor = descriptorsById.get(section.componentId);
    if (!descriptor) {
      issues.push({
        path: `${path}/componentId`,
        code: 'component.unknown',
        message: `${section.componentId} does not belong to ${answer.compositeId}`,
      });
      return;
    }
    if (observedIds.has(section.componentId)) {
      issues.push({
        path: `${path}/componentId`,
        code: 'component.duplicate',
        message: `${section.componentId} appears more than once`,
      });
    }
    observedIds.add(section.componentId);
    if (section.role !== descriptor.role) {
      issues.push({
        path: `${path}/role`,
        code: 'component.role_mismatch',
        message: `${section.componentId} must have role ${descriptor.role}`,
      });
    }
  };

  answer.sections.forEach((section, index) => {
    const path = `/sections/${index}`;
    checkDescriptor(section, path);
    const dataKeys = Object.keys(section.data);
    if (!sameStringSet(section.fields, dataKeys)) {
      issues.push({
        path: `${path}/fields`,
        code: 'component.fields_data_mismatch',
        message: 'fields must be the exact ordered index of keys present in data',
      });
    }
    const requiredFields = COMPONENT_SCHEMAS[section.componentId].attributes
      .filter(attribute => attribute.required)
      .map(attribute => attribute.name);
    requiredFields.forEach(field => {
      if (!section.fields.includes(field as never)) {
        issues.push({
          path: `${path}/fields`,
          code: 'component.required_field_missing',
          message: `${section.componentId} requires ${field}`,
        });
      }
    });
  });

  answer.omitted.forEach((section, index) => checkDescriptor(section, `/omitted/${index}`));

  descriptors.forEach(descriptor => {
    if (!observedIds.has(descriptor.componentId)) {
      issues.push({
        path: '/sections',
        code: 'component.unaccounted',
        message: `${descriptor.componentId} must appear in sections or omitted`,
      });
    }
  });

  const primaryId = composite.primarySection.componentId;
  const primaryIndex = answer.sections.findIndex(section => section.componentId === primaryId);
  if (answer.primaryFilled !== primaryIndex >= 0) {
    issues.push({
      path: '/primaryFilled',
      code: 'component.primary_filled_mismatch',
      message: 'primaryFilled must match whether the primary section was filled',
    });
  }
  if (primaryIndex > 0) {
    issues.push({
      path: `/sections/${primaryIndex}/componentId`,
      code: 'component.primary_not_first',
      message: 'The backend must place a filled primary section first',
    });
  }

  if (!sameJson(answer.slidePlans, composite.slidePlans)) {
    issues.push({
      path: '/slidePlans',
      code: 'layout.plan_mismatch',
      message: `slidePlans must match ${answer.compositeId} version ${answer.version}`,
    });
  }

  return issues;
};

export const validateCompositeAnswerV01 = (
  input: unknown,
  identity: CompositeAnswerIdentityV01
): CompositeAnswerValidationResult => {
  try {
    if (!validateSchema(input)) {
      return { ok: false, issues: schemaIssues(validateSchema.errors) };
    }
    const issues = semanticIssues(input, identity);
    return issues.length > 0 ? { ok: false, issues } : { ok: true, value: input };
  } catch {
    return {
      ok: false,
      issues: [
        {
          path: '',
          code: 'validation.unreadable_input',
          message: 'Input could not be read as a CompositeAnswer v0.1 value',
        },
      ],
    };
  }
};
